'use strict';

const Plaintext = window.Plaintext = (window.Plaintext || {});

/**
 * A numeric spinner tied to an <input>
 *
 * Fires the following events:
 * * Plaintext.Spinner.underflow: User attempted to decrement or manually change to below the minimum value
 * * Plaintext.Spinner.overflow: User attempted to increment or manually change to above the maximum value
 * * change: Because changes can be mediated by crement buttons, we can't rely on the native "change" event,
 *     so we manually fire a similar event on any change (and eat the original native change when necessary).
 *     Note that if you add a change listener before initializing the spinner, you may receive two change events
 *     (the native and the manual) when users update the native widget.
 */
Plaintext.Spinner = (function() {
  function enable(src) {
    const elements = Plaintext.Util.elementsFrom(src);
    elements.forEach(el => enableOn(el));
  }
  
  function enableOn(el) {
    if (el.tagName !== 'INPUT') {
      console.error("Could not create a Spinner on a non-input element", el);
      return;
    }
    
    // Already part of a spinner
    if (el.ptSpinner) { return; }
    
    const min = Plaintext.Util.undefinedOrEmptyToNull(el.min);
    const max = Plaintext.Util.undefinedOrEmptyToNull(el.max);
    
    el.value = el.value || min;
    
    const props = {
      min: (min === null) ? null : Number.parseInt(min),
      max: (max === null) ? null : Number.parseInt(max),
      value: el.value || 0,
      el: el
    };
    
    el.setAttribute('role', 'spinbutton');
    el.setAttribute('aria-valuenow', props.value);
    el.classList.add('pt-spinner');
    
    const wrap = document.createElement('div');
    wrap.classList.add('pt-spinner__wrap');
    el.after(wrap);
    
    const minusBtn = createCrementButton(el, '-', 'minus', -1, 'decrement');
    const plusBtn = createCrementButton(el, '+', 'plus', 1, 'increment');
    
    wrap.appendChild(minusBtn);
    wrap.appendChild(el);
    wrap.appendChild(plusBtn);
    
    el.addEventListener('change', onSpinnerChange, { capture: true });
    
    el.ptSpinner = props;
  }
  
  /**
   * Create an increment/decrement button
   */
  function createCrementButton(el, content, classSuffix, delta, label) {
    const button = document.createElement('button');
    
    // Remove from tab order, since the spinner supports up/down arrows for
    // anyone using a keyboard.
    button.tabIndex = -1;
    button.classList.add(`pt-spinner__crement--${ classSuffix }`);
    button.classList.add(`pt-spinner__crement`);
    button.dataset.delta = delta
    button.setAttribute('aria-label', label);
    button.setAttribute('type', 'button');
    
    const contentEl = document.createElement('span');
    contentEl.innerText = content;
    button.appendChild(contentEl);

    button.addEventListener('click', e => crement(el, delta));
    return button;
  }
  
  function crement(el, delta) {
    if (el.disabled) { return; }
    const newVal = parseInt(el.value) + delta;
    modifyValue(el, newVal);
  }
  
  function onSpinnerChange(e) {
    // We fire our own change events since changes can be initiated by crement clicks as well as normal
    // change events.  Prevent an infinite loop by detecting our custom change event and bailing.
    if (e.detail && e.detail['Plaintext.Spinner.changeEvent']) { return; }
    
    e.stopImmediatePropagation();
    
    modifyValue(e.target, e.target.value);
  }
  
  function modifyValue(el, newValue) {
    const min = el.ptSpinner.min;
    const events = [];
    
    if (min !== null && newValue < min) {
      events.push(new CustomEvent('Plaintext.Spinner.underflow', { detail: { rejectedValue: newValue, minValue: min }, bubbles: true }));
      newValue = min;
    }
    
    const max = el.ptSpinner.max;
    
    if (max !== null && newValue > max) {
      events.push(new CustomEvent('Plaintext.Spinner.overflow', { detail: { rejectedValue: newValue, maxValue: max }, bubbles: true }));
      newValue = max;
    }
    
    el.value = newValue;
    el.setAttribute('aria-valuenow', newValue);
    el.ptSpinner.value = newValue;

    const event = new CustomEvent('change', { detail: { 'Plaintext.Spinner.changeEvent': true }, bubbles: true });
    el.dispatchEvent(event);
    
    // Dispatch overflow/underflow events *after* the 'change' event so that handlers get an accurate
    // picture of the current state
    events.forEach(e => el.dispatchEvent(e));
  }
  
  return {
    enable
  }  
})();
